import React, { createContext, useContext, useCallback, useMemo, useState, useEffect, ReactNode } from 'react'
// ⛔ 直接指向 useAsyncData，不要走 @hooks/index 這個 barrel。
// barrel 會re-export useDepartmentLevelMap，而它又 import 本檔的 useLevelMap，
// 形成 hooks/index → useDepartmentLevelMap → LevelMapContext → hooks/index 的循環。
// 目前只因為引用發生在函式內才沒炸，但 bundler 換個模組順序就會是
// 「Cannot access useLevelMap before initialization」。
import { useAsyncData } from '@hooks/useAsyncData'
import { AUTH_CHANGED_EVENT } from '@/utils/authEvents'
import { getMyLevelMap, getLevelLabels } from '@/services/levelMapService'
import type { LevelTier, ResolvedLevelMap, LevelLabel, LevelLabelMap } from '@/types/levelMap'
import type { LevelColorTheme } from '@/constants/levelPalette'
import { levelData, FALLBACK_MAX_LEVEL } from '@pages/PartnerMap/data/levelData'

/**
 * 夥伴地圖 Context — 全站職等資訊的唯一入口
 *
 * 為什麼要 Context：LevelBadge 在夥伴清單、月薪頁、升等審核等地方一頁可能出現幾十次，
 * 每個都自己打一次 API 顯然不行。這裡在登入後載入一次，全站共用。
 *
 * 未登入或 API 失敗時回退到 levelData.ts 的出廠值，畫面不會空白。
 */

interface LevelMapContextType {
  map: ResolvedLevelMap
  isLoading: boolean
  /** 取單一職等；超出地圖範圍回 null */
  getTier: (level: number) => LevelTier | null
  /** 職等名稱，例：「專業夥伴」；查無時回 `LV{n}` */
  getLevelName: (level: number) => string
  /**
   * 取「指定夥伴」的職等名稱與顏色 —— 顯示**別人**的職等徽章時必須用這支。
   * 有個人指定地圖（levelMapId）時以它為準，否則依部門。
   *
   * ⛔ 別用 getTier()：那是登入者自己部門的地圖。夥伴清單、審核頁顯示的是別人的職等，
   * 用自己的地圖會把別部門同事的職等標成自己部門的名稱
   * （實測踩過：管理員在工程部，業務部同事的 LV2 被顯示成「工程專業夥伴」）。
   */
  getLabelFor: (
    department: string | null | undefined,
    level: number,
    levelMapId?: string | null
  ) => LevelLabel | null
  maxLevel: number
  reload: () => void
}

const LevelMapContext = createContext<LevelMapContextType | undefined>(undefined)

/** 出廠回退地圖的色彩主題（與 backend/src/constants/defaultLevelMap.ts 一致） */
const FALLBACK_THEME_BY_LEVEL: Record<number, LevelColorTheme> = {
  0: 'gray', 1: 'bronze', 2: 'amber', 3: 'gold', 4: 'emerald', 5: 'blue',
  6: 'purple', 7: 'pink', 8: 'red', 9: 'rainbow', 10: 'champagne',
  11: 'slate', 12: 'slate'
}

/** API 還沒回來時頂著用的出廠地圖 */
const FALLBACK_MAP: ResolvedLevelMap = {
  mapId: null,
  department: null,
  name: '夥伴升等地圖',
  maxLevel: FALLBACK_MAX_LEVEL,
  usingDefault: true,
  assignment: 'default',
  tiers: levelData.map(d => ({
    level: d.level,
    name: d.name,
    description: d.description,
    timeRequirement: d.timeRequirement,
    hourlyRate: d.hourlyRate,
    // 出廠回退值沒有逐級冷卻資訊，統一給 3；真正的值由後端地圖提供
    cooldownMonths: 3,
    requirements: d.requirements,
    // 出廠回退值沒有結構化的福利額度；真正的數字由後端地圖提供
    benefitAmounts: { COFFEE: 0, BIRTHDAY: 0, FITNESS: 0, BOOK_CLUB: 0 },
    benefits: d.benefits,
    colorTheme: FALLBACK_THEME_BY_LEVEL[d.level] ?? 'gray',
    tierStyle: d.isPartner
      ? 'PARTNER'
      : d.isLegendary
        ? 'LEGENDARY'
        : d.isSpecial
          ? 'SPECIAL'
          : null,
    isDraft: false,
    source: 'builtin' as const
  }))
}

export const LevelMapProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // ⛔ token 狀態必須放進 state 並訂閱 AUTH_CHANGED_EVENT，不能只在 render 時讀 localStorage。
  // App.tsx 裡的 children 是一次建立好的穩定元素，AuthProvider 因登入而 re-render 時
  // 這個 Provider 的 props 參照沒變 → React 會跳過整棵子樹 → 直接讀 localStorage 的值
  // 永遠停在登入前的 false，地圖一輩子不會載入，全站顯示出廠職等名稱直到手動重整。
  // 同樣的坑 SystemContext 已經踩過並記錄在案，這裡沿用它的解法。
  const [hasToken, setHasToken] = useState(() => !!localStorage.getItem('token'))

  useEffect(() => {
    const syncToken = () => setHasToken(!!localStorage.getItem('token'))
    window.addEventListener(AUTH_CHANGED_EVENT, syncToken)
    // 跨分頁登入／登出
    window.addEventListener('storage', syncToken)
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, syncToken)
      window.removeEventListener('storage', syncToken)
    }
  }, [])

  const { data, loading, reload } = useAsyncData(() => getMyLevelMap(), [hasToken], {
    enabled: hasToken,
    onError: (err: unknown) => console.warn('載入夥伴地圖失敗，改用出廠值:', err)
  })

  const map = data ?? FALLBACK_MAP

  const getTier = useCallback(
    (level: number) => map.tiers.find((t: LevelTier) => t.level === level) ?? null,
    [map]
  )

  const getLevelName = useCallback(
    (level: number) => map.tiers.find((t: LevelTier) => t.level === level)?.name ?? `LV${level}`,
    [map]
  )

  // 全部門的名稱／顏色對照表（不含時薪）——顯示別人的徽章時用
  const { data: labels, reload: reloadLabels } = useAsyncData<LevelLabelMap>(() => getLevelLabels(), [hasToken], {
    enabled: hasToken,
    onError: (err: unknown) => console.warn('載入職等名稱對照表失敗:', err)
  })

  const getLabelFor = useCallback(
    (department: string | null | undefined, level: number, levelMapId?: string | null): LevelLabel | null => {
      if (!labels) return null
      // 個人指定的地圖若已停用，byMap 裡不會有它 → 跟後端一樣回退到部門／預設
      const list =
        (levelMapId && labels.byMap?.[levelMapId]) ||
        (department && labels.byDepartment[department]) ||
        labels.default
      return list.find(l => l.level === level) ?? null
    },
    [labels]
  )

  // 後台改了地圖或指派後呼叫：自己的地圖與名稱對照表一起重載
  const reloadAll = useCallback(() => {
    reload()
    reloadLabels()
  }, [reload, reloadLabels])

  const value = useMemo<LevelMapContextType>(
    () => ({
      map,
      isLoading: loading,
      getTier,
      getLevelName,
      getLabelFor,
      maxLevel: map.maxLevel,
      reload: reloadAll
    }),
    [map, loading, getTier, getLevelName, getLabelFor, reloadAll]
  )

  return <LevelMapContext.Provider value={value}>{children}</LevelMapContext.Provider>
}

export const useLevelMap = (): LevelMapContextType => {
  const ctx = useContext(LevelMapContext)
  if (!ctx) {
    throw new Error('useLevelMap 必須在 LevelMapProvider 內使用')
  }
  return ctx
}
